import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import CloseIcon from '@mui/icons-material/Close';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import MenuIcon from '@mui/icons-material/Menu';
import AttachEmailIcon from '@mui/icons-material/AttachEmail';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import { Allotment } from 'allotment';
import 'allotment/dist/style.css';
import T from 'i18n';
import FileViewer from '../FileViewer';
import RightSidebarViewer from '../RightSidebarTabList';
import DocumentSearch from '../DocumentSearch';
import {
  selectBundleTabPage,
  selectCurrentCaseId,
  selectCurrentFolder,
  selectDocSearchLocation,
  selectDocSearchVal,
  selectIsGlobalPageLookup,
  selectViewerControl,
} from 'common/selectors';
import api from 'common/api';
import { useScrollToHighlight } from 'features/viewing/AcusoftViewer/AcusoftViewerControl/hooks/useScrollToHighlight';
import { setMouse } from 'features/viewing/AcusoftViewer/AcusoftViewerControl/utils';
import { MouseToolType } from 'features/viewing/AcusoftViewer/types';

const ATTACHMENT_NAME_MAX_LENGTH = 50;

type FocusViewProps = {
  open: boolean;
  onClose: () => void;
  onEnter?: () => void;
  onEntered?: () => void;
  onExit?: () => void;
  onExited: () => void;
  title?: string;
  pending: boolean;
  error: boolean;
  fileData: any | null;
  bundleSpecificFileDetails?: any;
  onViewerReady: () => void;
  onViewerError: () => void;
  onPrev: () => void;
  onNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
  prevTitle?: string | null;
  nextTitle?: string | null;
  zIndex?: number;
  isSidebarOpen?: boolean;
  onToggleSidebar?: () => void;
  onSelectDocument?: (document: any) => void;
};

const FocusView = ({
  open,
  onClose,
  onEnter,
  onEntered,
  onExit,
  onExited,
  title,
  pending,
  error,
  fileData,
  bundleSpecificFileDetails,
  onViewerReady,
  onViewerError,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  prevTitle,
  nextTitle,
  zIndex = 100000,
  isSidebarOpen = false,
  onToggleSidebar,
  onSelectDocument,
}: FocusViewProps) => {
  const displayTitle = title || T.translate('generic.view');
  const tooltipZIndex = zIndex + 2;
  const isBundleTabPage = useSelector(selectBundleTabPage);
  const currentCaseId = useSelector(selectCurrentCaseId);
  const currentFolder = useSelector(selectCurrentFolder);
  const isGlobalPageLookUp = useSelector(selectIsGlobalPageLookup) as boolean;
  const viewerControl = useSelector(selectViewerControl);
  const docSearchVal = useSelector(selectDocSearchVal);
  const docSearchLocation = useSelector(selectDocSearchLocation);
  const fallbackViewerControlRef = useRef({
    getAllMarks: () => [],
    scrollToAsync: () => Promise.resolve(),
  });
  const activeViewerControl = viewerControl || fallbackViewerControlRef.current;
  const { scrollSelectedHighlight, scrollToHighlight } = useScrollToHighlight({
    viewerControl: activeViewerControl,
  });
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);
  const canShowRightSidebar = !!fileData;
  const isRightSidebarVisible = rightSidebarOpen && canShowRightSidebar;
  const [hasEntered, setHasEntered] = useState(false);
  const [showAnnotationTools, setShowAnnotationTools] = useState(true);
  const [searchFunctions, setSearchFunctions] = useState<any>(null);
  const [attachmentsMenuAnchorEl, setAttachmentsMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [attachedToMenuAnchorEl, setAttachedToMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [fetchedAttachments, setFetchedAttachments] = useState<any[]>([]);
  const [fetchedAttachedTo, setFetchedAttachedTo] = useState<any[]>([]);
  const fileDataAttachments = Array.isArray(fileData?.attachments ?? fileData?.file?.attachments)
    ? fileData?.attachments ?? fileData?.file?.attachments
    : [];
  const fileDataAttachedTo = Array.isArray(fileData?.isAttachedTo ?? fileData?.file?.isAttachedTo)
    ? fileData?.isAttachedTo ?? fileData?.file?.isAttachedTo
    : [];
  const attachments = fetchedAttachments.length > 0 ? fetchedAttachments : fileDataAttachments;
  const attachedToDocuments = fetchedAttachedTo.length > 0 ? fetchedAttachedTo : fileDataAttachedTo;
  const hasAttachments = attachments.length > 0;
  const hasAttachedTo = attachedToDocuments.length > 0;
  const attachmentsMenuOpen = Boolean(attachmentsMenuAnchorEl);
  const attachedToMenuOpen = Boolean(attachedToMenuAnchorEl);
  const focusDocIdentity = fileData?.fileId || fileData?.id || fileData?.file?.id || null;
  const attachmentsHeadingLabel = T.translate('case.fileAttachments');
  const attachedToHeadingLabel = T.translate('generic.parentEmail');
  const attachmentsIdLabel = T.translate('generic.id');
  const attachmentsNameLabel = T.translate('generic.name');
  const shouldRenderViewer = hasEntered && !!fileData;
  const shouldHideViewer = pending || !hasEntered;
  const handleSearchFunctions = useCallback((functions: any) => {
    setSearchFunctions(functions);
  }, []);
  const handleSearch = useCallback(() => {}, []);
  const focusTitle = useMemo(() => {
    if (!fileData) return displayTitle;

    const docId = fileData?.fileId || fileData?.id || fileData?.file?.id || '';
    const docName =
      fileData?.name || fileData?.originalName || fileData?.fileName || fileData?.file?.name || '';
    const bundleMeta =
      bundleSpecificFileDetails && Object.keys(bundleSpecificFileDetails).length > 0
        ? bundleSpecificFileDetails
        : null;
    const globalPagePrefix =
      bundleMeta?.globalPagePrefix ||
      fileData?.globalPagePrefix ||
      fileData?.bundleLocations?.[0]?.globalPagePrefix ||
      '';
    const tabValue =
      bundleMeta?.tab ?? fileData?.tab ?? fileData?.bundleLocations?.[0]?.tab ?? '';

    let prefixLabel = '';
    if (isBundleTabPage) {
      if (globalPagePrefix) {
        prefixLabel = tabValue ? `${globalPagePrefix}/${tabValue}` : `${globalPagePrefix}`;
      }
    } else {
      const isGlobalPaging =
        currentFolder?.globalPaging || isGlobalPageLookUp || Boolean(globalPagePrefix);
      const startPage =
        bundleMeta?.startPage ??
        fileData?.startPage ??
        fileData?.bundleLocations?.[0]?.startPage ??
        '';
      const pageCount =
        bundleMeta?.pageCount ??
        fileData?.pageCount ??
        fileData?.bundleLocations?.[0]?.pageCount ??
        '';
      if (isGlobalPaging && startPage && pageCount && globalPagePrefix) {
        const startPageValue = String(startPage);
        const fileStartPage = startPageValue.includes('.')
          ? startPageValue.split('.')
          : startPageValue;
        const isStartPageFirstDigitAfterDecimalZero =
          Array.isArray(fileStartPage) &&
          fileStartPage.length > 1 &&
          fileStartPage[1].startsWith('0');
        const parsedPageCount = parseInt(String(pageCount), 10);
        if (Number.isFinite(parsedPageCount)) {
          let computedEndPage: number | string = '';
          if (startPageValue.includes('.') && Array.isArray(fileStartPage)) {
            const prefix = fileStartPage[0];
            const decimalPart = fileStartPage[1];
            const parsedDecimal = isStartPageFirstDigitAfterDecimalZero
              ? parseInt(decimalPart.slice(1), 10)
              : parseInt(decimalPart, 10);
            if (Number.isFinite(parsedDecimal)) {
              computedEndPage = `${prefix}.${isStartPageFirstDigitAfterDecimalZero ? '0' : ''}${
                parsedDecimal + parsedPageCount - 1
              }`;
            }
          } else {
            const parsedStart = parseInt(startPageValue, 10);
            if (Number.isFinite(parsedStart)) {
              computedEndPage = parsedStart + parsedPageCount - 1;
            }
          }
          if (computedEndPage !== '') {
            const tabSuffix = tabValue ? ` [Tab ${tabValue}]` : '';
            prefixLabel = `${globalPagePrefix}${startPageValue}-${computedEndPage}${tabSuffix}`;
          }
        }
      }
    }

    const parts: string[] = [];
    if (prefixLabel) {
      parts.push(`[${prefixLabel}]`);
    }
    if (docId) {
      parts.push(String(docId));
    }
    if (docName) {
      parts.push(String(docName));
    }

    return parts.length > 0 ? parts.join(' ') : displayTitle;
  }, [
    bundleSpecificFileDetails,
    currentFolder?.globalPaging,
    displayTitle,
    fileData,
    isBundleTabPage,
    isGlobalPageLookUp,
  ]);

  const handleSlideEnter = useCallback(() => {
    setHasEntered(false);
    if (onEnter) {
      onEnter();
    }
  }, [onEnter]);

  const handleSlideEntered = useCallback(() => {
    setHasEntered(true);
    if (onEntered) {
      onEntered();
    }
  }, [onEntered]);

  const handleSlideExit = useCallback(() => {
    setHasEntered(false);
    if (onExit) {
      onExit();
    }
  }, [onExit]);

  useEffect(() => {
    if (!open) {
      setRightSidebarOpen(false);
      setHasEntered(false);
      setShowAnnotationTools(true);
      setAttachmentsMenuAnchorEl(null);
      setAttachedToMenuAnchorEl(null);
      setFetchedAttachments([]);
      setFetchedAttachedTo([]);
    }
  }, [open]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (open) {
      document.body.classList.add('focus-view-portal-open');
    } else {
      document.body.classList.remove('focus-view-portal-open');
    }
    return () => {
      document.body.classList.remove('focus-view-portal-open');
    };
  }, [open]);

  useEffect(() => {
    if (!fileData) {
      setRightSidebarOpen(false);
    }
  }, [fileData]);

  useEffect(() => {
    setAttachmentsMenuAnchorEl(null);
    setAttachedToMenuAnchorEl(null);
  }, [focusDocIdentity]);

  useEffect(() => {
    if (!focusDocIdentity || !currentCaseId) {
      setFetchedAttachments([]);
      setFetchedAttachedTo([]);
      return;
    }
    let cancelled = false;
    (api as any)
      .get(`/cases/${currentCaseId}/files/${focusDocIdentity}/details?includeAttachments=true`)
      .then((res: any) => {
        if (cancelled || !res || res.error) return;
        const resAttachments = Array.isArray(res.attachments) ? res.attachments : [];
        const resAttachedTo = Array.isArray(res.isAttachedTo) ? res.isAttachedTo : [];
        setFetchedAttachments(resAttachments);
        setFetchedAttachedTo(resAttachedTo);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [focusDocIdentity, currentCaseId]);

  useEffect(() => {
    if (!hasAttachments) {
      setAttachmentsMenuAnchorEl(null);
    }
  }, [hasAttachments]);

  useEffect(() => {
    if (!hasAttachedTo) {
      setAttachedToMenuAnchorEl(null);
    }
  }, [hasAttachedTo]);

  const handleToggleAnnotationTools = useCallback(() => {
    setShowAnnotationTools((prev) => {
      const next = !prev;
      if (prev && !next && viewerControl?.setCurrentMouseTool) {
        setMouse(MouseToolType.PanAndEdit, false, viewerControl);
      }
      return next;
    });
  }, [viewerControl]);
  const handleAttachmentsMenuClose = useCallback(() => {
    setAttachmentsMenuAnchorEl(null);
  }, []);

  const handleAttachedToMenuClose = useCallback(() => {
    setAttachedToMenuAnchorEl(null);
  }, []);

  const handleAttachmentsIconClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!hasAttachments) return;
      if (attachmentsMenuAnchorEl) {
        handleAttachmentsMenuClose();
      } else {
        setAttachedToMenuAnchorEl(null);
        setAttachmentsMenuAnchorEl(event.currentTarget);
      }
    },
    [attachmentsMenuAnchorEl, handleAttachmentsMenuClose, hasAttachments],
  );

  const handleAttachedToIconClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!hasAttachedTo) return;
      if (attachedToMenuAnchorEl) {
        handleAttachedToMenuClose();
      } else {
        setAttachmentsMenuAnchorEl(null);
        setAttachedToMenuAnchorEl(event.currentTarget);
      }
    },
    [attachedToMenuAnchorEl, handleAttachedToMenuClose, hasAttachedTo],
  );

  const handleAttachmentMenuItemClick = useCallback(
    (document: any) => {
      handleAttachmentsMenuClose();
      onSelectDocument?.(document);
    },
    [handleAttachmentsMenuClose, onSelectDocument],
  );

  const handleAttachedToMenuItemClick = useCallback(
    (document: any) => {
      handleAttachedToMenuClose();
      onSelectDocument?.(document);
    },
    [handleAttachedToMenuClose, onSelectDocument],
  );

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      transitionDuration={{ enter: 0, exit: 200 }}
      ModalProps={{
        keepMounted: true,
        disableScrollLock: true,
        hideBackdrop: true,
        style: { zIndex, pointerEvents: 'none' },
      }}
      PaperProps={{
        style: {
          height: '100vh',
          width: '100vw',
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          zIndex: zIndex + 1,
          pointerEvents: 'auto',
        },
      }}
      sx={{ zIndex }}
      SlideProps={{
        onEnter: handleSlideEnter,
        onEntered: handleSlideEntered,
        onExit: handleSlideExit,
        onExited,
      }}
    >
      <div
        className={`focus-view${isSidebarOpen ? ' focus-view--sidebar-open' : ''}`}
        data-focus-view-root
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#fff',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
            }}
          >
            {onToggleSidebar && (
              <IconButton
                size="small"
                onClick={onToggleSidebar}
                aria-label={T.translate('generic.toggleSidebar')}
                aria-pressed={isSidebarOpen}
                style={{ marginRight: '0.25rem' }}
              >
                <MenuIcon fontSize="small" />
              </IconButton>
            )}
            <div
              style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}
            >
              <div
                style={{
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  color: '#1f2933',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  paddingRight: '0.5rem',
                  minWidth: 0,
                  flex: 1,
                }}
                title={focusTitle}
              >
                {focusTitle}
              </div>
              {(hasAttachments || hasAttachedTo) && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.2rem',
                    flexShrink: 0,
                  }}
                >
                  {hasAttachments && (
                    <Tooltip title={T.translate('generic.attachments')} placement="bottom" arrow>
                      <IconButton
                        size="small"
                        onClick={handleAttachmentsIconClick}
                        aria-label={T.translate('generic.attachments')}
                        aria-haspopup="true"
                        aria-expanded={attachmentsMenuOpen ? 'true' : undefined}
                        aria-controls={attachmentsMenuOpen ? 'focus-attachments-menu' : undefined}
                      >
                        <AttachEmailIcon sx={{ fontSize: '1rem', color: '#748CEC' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {hasAttachedTo && (
                    <Tooltip title={T.translate('generic.parentEmail')} placement="bottom" arrow>
                      <IconButton
                        size="small"
                        onClick={handleAttachedToIconClick}
                        aria-label={T.translate('generic.parentEmail')}
                        aria-haspopup="true"
                        aria-expanded={attachedToMenuOpen ? 'true' : undefined}
                        aria-controls={attachedToMenuOpen ? 'focus-attached-to-menu' : undefined}
                      >
                        <FilePresentIcon sx={{ fontSize: '1rem', color: '#6c757c' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={T.translate('filters.annotations')} placement="bottom" arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={handleToggleAnnotationTools}
                  disabled={!fileData}
                  aria-label={T.translate('filters.annotations')}
                  aria-pressed={showAnnotationTools}
                  style={{ marginRight: '0.25rem' }}
                >
                  <BorderColorIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton
              size="small"
              onClick={() => setRightSidebarOpen((prev) => !prev)}
              disabled={!canShowRightSidebar}
              aria-label={T.translate('generic.toggleRightSidebar')}
              aria-pressed={rightSidebarOpen}
              style={{ marginRight: '0.25rem' }}
            >
              <MenuIcon fontSize="small" style={{ transform: 'scaleX(-1)' }} />
            </IconButton>
            <IconButton size="small" onClick={onClose} aria-label={T.translate('generic.close')}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
        <Menu
          id="focus-attachments-menu"
          anchorEl={attachmentsMenuAnchorEl}
          open={attachmentsMenuOpen}
          onClose={handleAttachmentsMenuClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          MenuListProps={{
            dense: true,
          }}
          disableScrollLock
          sx={{ zIndex: zIndex + 3 }}
          PaperProps={{
            sx: {
              maxHeight: 350,
              minWidth: 220,
              py: 0,
              border: '1px solid #cfcfcf',
            },
          }}
        >
          <div
            style={{
              padding: '15px 10px',
              fontWeight: 600,
              fontSize: '14px',
              color: '#5c5c5c',
            }}
          >
            {attachmentsHeadingLabel}
          </div>
          {attachments.map((attachment: any, index: number) => {
            const attachmentId = attachment?.id || '-';
            const rawName = typeof attachment?.name === 'string' ? attachment.name.trim() : '';
            const shouldTruncateName = rawName.length > ATTACHMENT_NAME_MAX_LENGTH;
            const truncatedName = shouldTruncateName
              ? rawName.slice(0, ATTACHMENT_NAME_MAX_LENGTH)
              : rawName;

            return (
              <MenuItem
                key={attachment?.id || `${attachment?.name || 'attachment'}-${index}`}
                onClick={() => handleAttachmentMenuItemClick(attachment)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '2px',
                  padding: '8px',
                }}
              >
                <span style={{ fontWeight: 600, fontSize: '12px' }}>
                  {`${attachmentsIdLabel}: ${attachmentId}`}
                </span>
                <Tooltip title={shouldTruncateName ? rawName : ''} arrow placement="top">
                  <span
                    style={{
                      color: '#4a4a4a',
                      display: 'flex',
                      fontSize: '12px',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    {`${attachmentsNameLabel}: ${truncatedName || '-'}`}
                    {shouldTruncateName && (
                      <span
                        style={{
                          fontWeight: 600,
                          lineHeight: 1,
                        }}
                      >
                        ...
                      </span>
                    )}
                  </span>
                </Tooltip>
              </MenuItem>
            );
          })}
        </Menu>
        <Menu
          id="focus-attached-to-menu"
          anchorEl={attachedToMenuAnchorEl}
          open={attachedToMenuOpen}
          onClose={handleAttachedToMenuClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          MenuListProps={{
            dense: true,
          }}
          disableScrollLock
          sx={{ zIndex: zIndex + 3 }}
          PaperProps={{
            sx: {
              maxHeight: 350,
              minWidth: 220,
              py: 0,
              border: '1px solid #cfcfcf',
            },
          }}
        >
          <div
            style={{
              padding: '15px 10px',
              fontWeight: 600,
              fontSize: '14px',
              color: '#5c5c5c',
            }}
          >
            {attachedToHeadingLabel}
          </div>
          {attachedToDocuments.map((attachment: any, index: number) => {
            const attachmentId = attachment?.id || '-';
            const rawName = typeof attachment?.name === 'string' ? attachment.name.trim() : '';
            const shouldTruncateName = rawName.length > ATTACHMENT_NAME_MAX_LENGTH;
            const truncatedName = shouldTruncateName
              ? rawName.slice(0, ATTACHMENT_NAME_MAX_LENGTH)
              : rawName;

            return (
              <MenuItem
                key={attachment?.id || `${attachment?.name || 'attached-to'}-${index}`}
                onClick={() => handleAttachedToMenuItemClick(attachment)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '2px',
                  padding: '8px',
                }}
              >
                <span style={{ fontWeight: 600, fontSize: '12px' }}>
                  {`${attachmentsIdLabel}: ${attachmentId}`}
                </span>
                <Tooltip title={shouldTruncateName ? rawName : ''} arrow placement="top">
                  <span
                    style={{
                      color: '#4a4a4a',
                      display: 'flex',
                      fontSize: '12px',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    {`${attachmentsNameLabel}: ${truncatedName || '-'}`}
                    {shouldTruncateName && (
                      <span
                        style={{
                          fontWeight: 600,
                          lineHeight: 1,
                        }}
                      >
                        ...
                      </span>
                    )}
                  </span>
                </Tooltip>
              </MenuItem>
            );
          })}
        </Menu>
        <div className="view-drawer-viewer">
          <Allotment className="custom-allotment focus-view-allotment" defaultSizes={[72, 28]}>
            <Allotment.Pane minSize={200}>
              <div className="viewer-document-card">
                {pending && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#fff',
                      zIndex: 1,
                    }}
                  >
                    <CircularProgress />
                  </div>
                )}
                {error ? (
                  <div
                    style={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#6c757c',
                      fontSize: '1rem',
                    }}
                  >
                    Error occurred
                  </div>
                ) : (
                  <div
                    style={{
                      height: '100%',
                      visibility: shouldHideViewer ? 'hidden' : 'visible',
                    }}
                  >
                    {shouldRenderViewer ? (
                      <FileViewer
                        fileData={fileData}
                        currentFileMetaData={fileData}
                        isDocFinder={false}
                        isPreviewMode={!showAnnotationTools}
                        annotationToolsEnabled={showAnnotationTools}
                        hidePresentButton
                        bundleSpecificFileDetails={bundleSpecificFileDetails}
                        searchHandler={handleSearch}
                        searchFunctionsHandler={handleSearchFunctions}
                        waitForFirstScaleAfterFit
                        enableSidebarAnnotations
                        sideSummaryOpen={false}
                        setSideSummaryOpen={() => {}}
                        onViewerReady={onViewerReady}
                        onViewerError={onViewerError}
                      />
                    ) : null}
                    {searchFunctions && (docSearchVal || docSearchLocation) ? (
                      <DocumentSearch
                        searchFunctions={searchFunctions}
                        docSearchVal={docSearchVal}
                        docSearchLocation={docSearchLocation}
                        isAiTools={false}
                        isSearchMode={false}
                        sideSummaryOpen={false}
                      />
                    ) : null}
                  </div>
                )}
                <Tooltip
                  title={prevTitle || ''}
                  placement="top"
                  arrow
                  disableHoverListener={!prevTitle}
                  PopperProps={{ style: { zIndex: tooltipZIndex } }}
                >
                  <span className="viewer-document-nav viewer-document-nav--prev">
                    <IconButton
                      className="viewer-document-nav-button"
                      onClick={onPrev}
                      disabled={!hasPrev}
                      aria-label={T.translate('generic.prevDoc')}
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={nextTitle || ''}
                  placement="top"
                  arrow
                  disableHoverListener={!nextTitle}
                  PopperProps={{ style: { zIndex: tooltipZIndex } }}
                >
                  <span className="viewer-document-nav viewer-document-nav--next">
                    <IconButton
                      className="viewer-document-nav-button"
                      onClick={onNext}
                      disabled={!hasNext}
                      aria-label={T.translate('generic.nextDoc')}
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </Allotment.Pane>
            <Allotment.Pane minSize={220} visible={isRightSidebarVisible}>
              <div className="focus-view-right-sidebar">
                {isRightSidebarVisible ? (
                  <RightSidebarViewer
                    selectedHighlight={scrollSelectedHighlight}
                    highlights={[]}
                    scrollToHighlight={scrollToHighlight}
                    fileData={fileData}
                    viewerControl={viewerControl}
                    gridView={false}
                    defaultTab="annotations"
                  />
                ) : null}
              </div>
            </Allotment.Pane>
          </Allotment>
        </div>
      </div>
    </Drawer>
  );
};

export default FocusView;
